import { useEffect, useState } from 'react';
import { Wallet, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { usePrivy, useLogin } from '@privy-io/react-auth';
import { Avatar } from '@/components/ui/Avatar';
import { useAuthStore } from '@/store/authStore';
import { fetchProfile, getStoredToken, loginWithPrivy } from '@/lib/api';
// import { useRail1Link } from '@/hooks/useRail1Link';
import { isPrivyReady } from '@/lib/runtime';
import type { UserProfile } from '@/lib/types';
import { cn, truncateAddress } from '@/lib/utils';

export interface ConnectButtonProps {
  /** Compact mode renders a tighter chip with no address copy. */
  compact?: boolean;
  className?: string;
}

/**
 * Connect CTA. Logs in through Privy (email / wallet / social per the Privy
 * dashboard), exchanges the Privy access token for our backend session JWT
 * (POST /auth/privy), then mirrors the profile into the auth store so the rest
 * of the UI flips to the authenticated state.
 *
 * Privy hooks are only valid inside <PrivyProvider>, which mounts only when the
 * backend (and its Privy app id) is reachable — see main.tsx. So the sign-in
 * CTA that uses those hooks (`PrivySignIn`) is rendered only when the provider
 * is in the tree; otherwise we fall back to a no-op CTA.
 */
function ConnectButton({ compact = false, className }: ConnectButtonProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const profile = useAuthStore((s) => s.profile);
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setProfile = useAuthStore((s) => s.setProfile);

  // Restore session on mount when a JWT is present but state is stale —
  // re-fetch /auth/me; if the token is rejected, fall back to disconnected.
  useEffect(() => {
    if (isAuthenticated || !getStoredToken()) return;
    setAuthenticated(true);
    if (profile) return;
    fetchProfile()
      .then((p) => setProfile(p))
      .catch(() => {
        setAuthenticated(false);
        setProfile(null);
      });
  }, [isAuthenticated, profile, setAuthenticated, setProfile]);

  if (isAuthenticated && profile) {
    return <AuthedChip profile={profile} compact={compact} className={className} />;
  }

  return isPrivyReady() ? (
    <PrivySignIn compact={compact} className={className} />
  ) : (
    <DisabledConnect compact={compact} className={className} />
  );
}

// ── Authenticated chip ──────────────────────────────────────────────────────

function AuthedChip({
  profile,
  compact,
  className,
}: {
  profile: UserProfile;
  compact: boolean;
  className?: string;
}) {
  const display = profile.ensName ?? profile.displayName ?? truncateAddress(profile.address);

  if (compact) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-2 rounded-full border border-line bg-paper-warm px-2.5 py-1.5',
          className,
        )}
        aria-label={`Signed in as ${display}`}
      >
        <Avatar src={profile.avatarUrl} name={profile.displayName} size={22} />
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-forest-500" aria-hidden="true" />
      </span>
    );
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full border border-line bg-paper-warm px-3 py-1.5 text-sm font-medium text-ink-950',
        className,
      )}
    >
      <Avatar src={profile.avatarUrl} name={profile.displayName} size={22} />
      <span className="flex min-w-0 flex-col leading-tight">
        <span className="max-w-[160px] truncate text-sm">{display}</span>
        <span className="max-w-[160px] truncate font-mono text-[10px] text-slate2-500">
          {truncateAddress(profile.address)}
        </span>
      </span>
    </span>
  );
}

// ── Presentational CTA ──────────────────────────────────────────────────────

function ConnectCTA({
  compact,
  className,
  loading,
  error,
  onClick,
}: {
  compact: boolean;
  className?: string;
  loading: boolean;
  error: string | null;
  onClick: () => void;
}) {
  return (
    <div className={cn('flex flex-col items-stretch gap-1', className)}>
      <button
        type="button"
        onClick={onClick}
        disabled={loading}
        aria-label="Connect"
        className={cn('btn-water', compact ? 'px-3 py-2 text-sm' : '', 'w-full justify-center')}
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Wallet className="h-4 w-4" aria-hidden="true" />
        )}
        <span>{loading ? 'Connecting…' : 'Connect'}</span>
      </button>
      {error && (
        <span role="alert" className="mt-1 text-xs text-rose2-600">
          {error}
        </span>
      )}
    </div>
  );
}

// ── Privy-powered sign-in (only mounted when PrivyProvider is present) ────────

function PrivySignIn({ compact, className }: { compact: boolean; className?: string }) {
  const qc = useQueryClient();
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const setProfile = useAuthStore((s) => s.setProfile);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { ready, authenticated, getAccessToken } = usePrivy();
  // const rail1Link = useRail1Link();

  // Session restore (a stored JWT from a previous visit): re-check the rail1
  // link so an admin/superadmin session re-triggers the gate overlay on
  // refresh, not just right after an interactive login. `linkRail1` is
  // single-flight + idempotent, so this is safe even with several
  // ConnectButton instances mounted at once.
  // useEffect(() => {
  //   if (getStoredToken()) void rail1Link();
  // }, [rail1Link]);

  // Exchange the current Privy session for our backend JWT, then hydrate the
  // profile from /auth/me (the authoritative source for rank / completions).
  const exchange = async (): Promise<void> => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No Privy session');
      await loginWithPrivy(token);
      // Best-effort: login must succeed even if bigwater_backend is down.
      // Failure surfaces via rail1LinkStatus (see Rail1LinkBadge), not here.
      // void rail1Link();
      const p = await fetchProfile();
      setProfile(p);
      setAuthenticated(true);
      qc.invalidateQueries({ queryKey: ['quests'] });
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['leaderboard'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    } finally {
      setLoading(false);
    }
  };

  const { login } = useLogin({
    onComplete: () => {
      void exchange();
    },
    onError: (err) => {
      setLoading(false);
      // User closing the modal isn't an error worth surfacing.
      if (err !== 'exited_auth_flow') {
        setError(typeof err === 'string' ? err : 'Connection failed');
      }
    },
  });

  const onSignIn = (): void => {
    if (!ready) return;
    setError(null);
    setLoading(true);
    // Already authenticated with Privy (e.g. JWT expired but Privy session
    // alive) → skip the modal and re-exchange directly.
    if (authenticated) void exchange();
    else login();
  };

  return (
    <ConnectCTA
      compact={compact}
      className={className}
      loading={loading}
      error={error}
      onClick={onSignIn}
    />
  );
}

// ── Fallback when no backend / no Privy provider (mock mode, offline) ─────────

function DisabledConnect({ compact, className }: { compact: boolean; className?: string }) {
  const [error, setError] = useState<string | null>(null);
  return (
    <ConnectCTA
      compact={compact}
      className={className}
      loading={false}
      error={error}
      onClick={() => setError('Backend not configured')}
    />
  );
}

export default ConnectButton;
